import React, { useCallback, useMemo } from 'react';
import { IoIosClose } from 'react-icons/io';
import { generateId } from '@utils';
import { FilterObject, PropertyAddFilterProps } from '@interface';
import {
	generalConditionOptions,
	filterOptions,
	timeOptions,
	classOptions,
	yesNoOptions,
	statusOptions,
	typeOptions,
	saleTypeOptions,
	filterOptionMap,
	yesNoFilterTypes,
	enforcementStatusOptions,
} from '@/mockData/customSearch';

export const PropertyAddFilter: React.FC<PropertyAddFilterProps> = ({
	filters,
	setFilters,
	logicalOperator,
	setLogicalOperator,
	showFilterDropdown,
	setShowFilterDropdown,
}) => {
	// Current date string - memoized to avoid recalculation
	const currentDate = useMemo(() => new Date().toISOString().split('T')[0], []);

	// Memoized filter creation function
	const createNewFilter = useCallback(
		(filterValue: string): FilterObject => {
			if (yesNoFilterTypes.includes(filterValue)) {
				return {
					id: generateId(),
					filterType: filterValue,
					value: 'yes',
					options: {},
				};
			}

			return {
				id: generateId(),
				conditionType: 'atLeast',
				count: 1,
				filterType: filterValue,
				timeCondition: 'since',
				date: currentDate,
				endDate: currentDate,
				options: {},
			};
		},
		[currentDate]
	);

	// Callback functions
	const handleApplyFilter = useCallback(
		(filterValue: string) => {
			setFilters((prevFilters) => [
				...prevFilters,
				createNewFilter(filterValue),
			]);
			setShowFilterDropdown(false);
		},
		[createNewFilter]
	);

	const handleRemoveFilter = useCallback((id: string) => {
		setFilters((prevFilters) =>
			prevFilters.filter((filter) => filter.id !== id)
		);
	}, []);

	const handleOptionSelect = useCallback(
		(filterId: string, optionType: string, value: string) => {
			setFilters((prevFilters) =>
				prevFilters.map((filter) =>
					filter.id === filterId
						? {
								...filter,
								options: {
									...filter.options,
									[optionType]: value,
								},
							}
						: filter
				)
			);
		},
		[]
	);

	const handleFilterChange = useCallback(
		(index: number, field: keyof FilterObject, value: any) => {
			setFilters((prevFilters) => {
				const newFilters = [...prevFilters];
				if (field === 'filterType') {
					// Reset options when filter type changes
					newFilters[index] = {
						...newFilters[index],
						[field]: value,
						options: {},
					};
				} else {
					newFilters[index] = {
						...newFilters[index],
						[field]: value,
					};
				}
				return newFilters;
			});
		},
		[]
	);

	// Memoized rendering function for options
	const renderOptions = useCallback(
		(filter: FilterObject) => {
			const { filterType, id } = filter;
			const options = filterOptionMap[filterType] || [];

			return options.map((option) => {
				switch (option) {
					case 'class':
						return (
							<div key="class" className="mt-2 flex items-center space-x-2">
								<div>
									<span className="text-blue-600 hover:underline">Class</span>
								</div>
								<div>
									<select
										defaultValue=""
										className="border-b outline-none"
										onChange={(e) =>
											handleOptionSelect(id, 'class', e.target.value)
										}
									>
										<option value="" disabled>
											Select Class
										</option>
										{classOptions.map((opt) => (
											<option key={opt.value} value={opt.value}>
												{opt.label}
											</option>
										))}
									</select>
								</div>
							</div>
						);

					case 'status':
						return (
							<div key="status" className="mt-2 flex items-center space-x-2">
								<div>
									<span className="text-blue-600 hover:underline">Status</span>
								</div>
								<div className="flex items-center space-x-2">
									<select
										defaultValue=""
										className="border-b outline-none"
										onChange={(e) =>
											handleOptionSelect(id, 'status', e.target.value)
										}
									>
										<option value="" disabled>
											Select Status
										</option>
										{(filterType === 'alternativeEnforcement'
											? enforcementStatusOptions
											: statusOptions
										).map((opt) => (
											<option key={opt.value} value={opt.value}>
												{opt.label}
											</option>
										))}
									</select>
								</div>
							</div>
						);

					case 'type':
						return (
							<div key="type" className="mt-2 flex items-center space-x-2">
								<div>
									<span className="text-blue-600 hover:underline">Type</span>
								</div>
								<div>
									<select
										defaultValue=""
										className="border-b outline-none"
										onChange={(e) =>
											handleOptionSelect(id, 'type', e.target.value)
										}
									>
										<option value="" disabled>
											Select Type
										</option>
										{(filterType === 'marshalEvictions'
											? saleTypeOptions
											: typeOptions
										).map((opt) => (
											<option key={opt.value} value={opt.value}>
												{opt.label}
											</option>
										))}
									</select>
								</div>
							</div>
						);

					case 'salePrice':
						return (
							<div key="salePrice" className="mt-2 flex items-center">
								<span className="text-blue-600 hover:underline">
									Sale Price
								</span>
								<div className="flex items-center space-x-2">
									<select
										defaultValue=""
										className="border-b outline-none"
										onChange={(e) =>
											handleOptionSelect(id, 'salePrice', e.target.value)
										}
									>
										<option value="" disabled>
											Select Sale Price
										</option>
										{generalConditionOptions.map((opt) => (
											<option key={opt.value} value={opt.value}>
												{opt.label}
											</option>
										))}
									</select>
									<input
										type="number"
										className="border-b outline-none w-20"
										placeholder="Amount"
										onChange={(e) =>
											handleOptionSelect(id, 'saleAmount', e.target.value)
										}
									/>
								</div>
							</div>
						);
					default:
						return null;
				}
			});
		},
		[handleOptionSelect]
	);

	// Render the main filter controls differently for Yes/No filter types
	const renderFilterControls = (filter: FilterObject, index: number) => {
		const isYesNoFilter = yesNoFilterTypes.includes(filter.filterType);

		return (
			<div className="flex items-center space-x-2">
				{isYesNoFilter ? (
					<>
						<div>
							{filter.filterType === 'taxLien' && <span>Has Tax Lien?</span>}
							{filter.filterType === 'noHarassment' && (
								<span>Eligible for certificate of no harassment?</span>
							)}
							{filter.filterType === 'alternativeEnforcement' && (
								<span>In Alternative Enforcement Program?</span>
							)}
						</div>
						<div>
							<select
								defaultValue=""
								className="border-b outline-none"
								onChange={(e) =>
									handleFilterChange(index, 'value', e.target.value)
								}
							>
								{yesNoOptions.map((opt) => (
									<option key={opt.value} value={opt.value}>
										{opt.label}
									</option>
								))}
							</select>
						</div>
					</>
				) : (
					<>
						<select
							className="border-b pb-2 outline-none"
							value={filter.conditionType}
							onChange={(e) =>
								handleFilterChange(index, 'conditionType', e.target.value)
							}
						>
							{generalConditionOptions.map((option) => (
								<option key={option.value} value={option.value}>
									{option.label}
								</option>
							))}
						</select>
						<input
							type="number"
							className="pl-4 border-b pb-2 w-16 outline-none"
							value={filter.count}
							min="1"
							onChange={(e) =>
								handleFilterChange(
									index,
									'count',
									parseInt(e.target.value) || 1
								)
							}
						/>
						<select
							className="border-b pb-2 outline-none"
							value={filter.filterType}
							onChange={(e) =>
								handleFilterChange(index, 'filterType', e.target.value)
							}
						>
							{filterOptions.map((option) => (
								<option key={option.value} value={option.value}>
									{option.label}
								</option>
							))}
						</select>
						<select
							className="border-b pb-2 outline-none"
							value={filter.timeCondition}
							onChange={(e) =>
								handleFilterChange(index, 'timeCondition', e.target.value)
							}
						>
							{timeOptions.map((option) => (
								<option key={option.value} value={option.value}>
									{option.label}
								</option>
							))}
						</select>
						<input
							type="date"
							className="border-b pb-2 outline-none"
							value={filter.date}
							onChange={(e) =>
								handleFilterChange(index, 'date', e.target.value)
							}
						/>
						{filter.timeCondition === 'between' && (
							<>
								<span className="text-gray-500">and</span>
								<input
									type="date"
									className="border-b pb-2 outline-none"
									value={filter.endDate}
									onChange={(e) =>
										handleFilterChange(index, 'endDate', e.target.value)
									}
								/>
							</>
						)}
					</>
				)}

				<button
					className="ml-2 text-gray-500 hover:text-gray-700"
					onClick={() => handleRemoveFilter(filter.id)}
				>
					<IoIosClose className="h-8 w-8" />
				</button>
			</div>
		);
	};

	return (
		<div className="p-4 mx-auto text-[15px]">
			{filters.map((filter, index) => (
				<div key={filter.id} className="mb-4">
					<div className="flex items-center justify-between">
						<div className="flex-1">
							{renderFilterControls(filter, index)}
							<div className="mt-2">{renderOptions(filter)}</div>
						</div>
					</div>
					{index < filters.length - 1 && (
						<div className="mt-2 text-center">
							<select
								className="border-b outline-none"
								value={logicalOperator}
								onChange={(e) =>
									setLogicalOperator(e.target.value as 'AND' | 'OR')
								}
							>
								<option value="AND">AND</option>
								<option value="OR">OR</option>
							</select>
						</div>
					)}
				</div>
			))}

			{filters.length === 0 && !showFilterDropdown && (
				<button
					className="text-blue-600 hover:underline text-[14px]"
					onClick={() => setShowFilterDropdown(true)}
				>
					+ New Filter
				</button>
			)}

			{showFilterDropdown && (
				<div className="flex space-x-4 items-center">
					<select
						className="border-b pb-2 outline-none"
						onChange={(e) => handleApplyFilter(e.target.value)}
						defaultValue=""
					>
						<option value="" disabled>
							New Filter
						</option>
						{filterOptions.map((option) => (
							<option key={option.value} value={option.value}>
								{option.label}
							</option>
						))}
					</select>

					<button
						className="ml-2 text-gray-500 hover:text-gray-700"
						onClick={() => setShowFilterDropdown(false)}
					>
						<IoIosClose className="h-8 w-8" />
					</button>
				</div>
			)}

			{filters.length > 0 && !showFilterDropdown && (
				<div>
					<button
						className="text-blue-600 hover:underline text-[14px]"
						onClick={() => setShowFilterDropdown(true)}
					>
						+ {logicalOperator}
					</button>
				</div>
			)}
		</div>
	);
};
